// Conditional-rendering predicate evaluator for questions.
//
// Predicate shape. any of:
//   { key: 'required_information.industry', equals: 'saas' }
//   { key: 'required_information.payment_models', includesAny: ['recurring','installments'] }
//   { key: 'required_information.product_types', includes: 'physical_goods' }
//   { key: 'checkout_policies.free_trial_offered', notEquals: 'no' }
//   { not: <predicate> }
//   { any: [<predicate>, ...] }            // OR
//   [<predicate>, ...]                     // AND
//
// `key` references the building_blocks JSON using dot notation that always
// starts with the section name so we can branch on cross-section answers.

const getDeep = (obj, path) => {
  if (!path || !obj) return undefined
  return path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj)
}

export const evalShowIf = (predicate, buildingBlocks) => {
  if (predicate == null) return true
  if (Array.isArray(predicate)) return predicate.every((p) => evalShowIf(p, buildingBlocks))
  if (predicate.any) return predicate.any.some((p) => evalShowIf(p, buildingBlocks))
  if (predicate.not) return !evalShowIf(predicate.not, buildingBlocks)
  const value = getDeep(buildingBlocks, predicate.key)
  if ("equals" in predicate) return value === predicate.equals
  // An unanswered parent (undefined / null / '') must NOT reveal a notEquals
  // follow-up: `notEquals: 'never'` means "they picked something other than
  // never", which is false until they have actually picked anything.
  if ("notEquals" in predicate)
    return value != null && value !== "" && value !== predicate.notEquals
  if ("includes" in predicate) return Array.isArray(value) && value.includes(predicate.includes)
  if ("includesAny" in predicate)
    return Array.isArray(value) && predicate.includesAny.some((v) => value.includes(v))
  if ("includesAll" in predicate)
    return Array.isArray(value) && predicate.includesAll.every((v) => value.includes(v))
  if ("present" in predicate) {
    const present = !(value == null || value === "" || (Array.isArray(value) && value.length === 0))
    return predicate.present ? present : !present
  }
  return true
}

export const visibleQuestions = (schema, buildingBlocks) =>
  (schema || []).filter((q) => evalShowIf(q.showIf, buildingBlocks))

// Per-option showIf: a chip-multi / select option can carry its own showIf
// predicate. Used to hide e.g. the "Shipping" chip from "Which policies do
// you have?" when the merchant's industry is SaaS. the field itself is
// still relevant, but that specific option isn't.
export const visibleOptions = (question, buildingBlocks) =>
  (question?.options || []).filter((o) => evalShowIf(o.showIf, buildingBlocks))
